import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import { MessageService } from '@openng/optimus-ui/api';
import { Optimus } from '@openng/optimus-ui/config';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { GraphQlService } from '~ngx-shared/graph-ql';
import { LayoutConfig, LayoutService, LoadingService, MenuService } from '~ngx-shared/layout';
import { FileService, VersionService } from '~ngx-shared/services';
import { environment } from '~shared/environment';
import { NotificationService } from '~shared/notification/services/notification.service';
import { UserService } from '~shared/services';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';
import { EducationService } from './services/education.service';
import { EventService } from './services/event.service';
import { HearingMenuService } from './services/hearing-menu.service';
import { PendingService } from './services/pending.service';
import { Education } from './theme/education';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly layoutService = inject(LayoutService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly versionService = inject(VersionService);
  private readonly pendingService = inject(PendingService);
  private readonly hearingMenuService = inject(HearingMenuService);
  private readonly eventService = inject(EventService);
  private readonly educationService = inject(EducationService);
  private readonly messageService = inject(MessageService);
  private readonly notificationService = inject(NotificationService);
  private readonly optimusConfig = inject(Optimus);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly graphQlService = inject(GraphQlService);

  constructor() {
    const authenticationService = inject(AuthenticationService);
    const userService = inject(UserService);
    const fileService = inject(FileService);

    fileService.init(
      `${environment.externalServiceUrl}/file/upload`,
      `${environment.externalServiceUrl}/file`
    );

    effect(() => {
      const isNewAvailable = this.versionService.isNewVersionAvailable();
      if (isNewAvailable) {
        this.messageService.add({
          severity: 'error',
          summary: 'update',
          closable: false,
          sticky: true,
          key: 'new_version_available'
        });
      }
    });

    effect(() => {
      const keycloakEvent = this.keycloakSignal();
      if (keycloakEvent.type === KeycloakEventType.Ready) {
        authenticationService.init(
          ['default-roles-iggoe', 'iggoe-user'],
          url =>
            url.startsWith(environment.hasuraUrl) || url.startsWith(environment.externalServiceUrl)
        );
        this.authorizationService.setAllowedRoles(
          Object.values(AuthorizationUtil.GROUP_EDUCATION_ALL)
        );
        userService.init();
      }
    });
  }

  ngOnInit(): void {
    this.loadingService.startLoading();

    //optional configuration with the default configuration
    const config: LayoutConfig = {
      preset: 'Aura',
      primary: 'emerald',
      surface: null,
      darkTheme: false,
      menuMode: 'static'
    };
    this.layoutService.layoutConfig.set(config);
    this.optimusConfig.theme.set({
      ...this.optimusConfig.theme(),
      preset: Education
    });

    this.translocoService.selectTranslateObject('primeng').subscribe(translated => {
      this.optimusConfig.setTranslation(translated);
    });

    this.menuService.sideBarItems$.next([
      {
        id: 'home',
        label: 'Home',
        items: [
          {
            label: 'dashboard',
            icon: 'pi pi-fw pi-home',
            routerLink: ['/', 'dashboard']
          },
          {
            label: 'profile',
            icon: 'pi pi-fw pi-user',
            routerLink: ['/', 'profile']
          }
        ]
      },
      {
        label: 'education.my.teaching_organisation',
        items: [
          {
            label: 'education.groups',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'groups']
          },
          {
            label: 'education.timetables',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'timetables']
          },
          {
            label: 'education.planning_forms',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'planning-forms']
          },
          {
            label: 'education.observation_sheets',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'observation-sheets']
          }
        ],
        state: { permissions: [RoleEnum.TEACHER] }
      },
      {
        id: 'hearing-scoring',
        label: 'education.hearing.commission',
        visible: false,
        items: [
          {
            label: 'education.hearing.my_hearings',
            icon: 'pi pi-fw pi-check-square',
            routerLink: ['/', 'my', 'hearing-scoring']
          }
        ]
      },
      {
        label: 'shared.my_processes',
        items: [
          {
            label: 'shared.service_contracts',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'service-contracts'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'education.letter_of_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'letter-of-services'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'shared.absences',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'absences'],
            state: { permissions: [RoleEnum.TEACHER, RoleEnum.INSPECTOR] }
          },
          {
            label: 'education.part_time_jobs',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'part-time-jobs'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'education.transfer_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'transfer-services'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'education.addendum_igazas',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'addendum-igazas'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'shared.declaration_of_commitment_il_pd.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'declaration-of-commitment-il-pds'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'education.declaration_of_commitment.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'declaration-of-commitments'],
            state: { permissions: RoleEnum.TEACHER }
          },
          {
            label: 'shared.child_allowance_application.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'child-allowance-applications'],
            state: { permissions: RoleEnum.TEACHER }
          }
        ],
        state: { permissions: [RoleEnum.INSPECTOR, RoleEnum.TEACHER] }
      },
      {
        label: 'shared.personnel_service_law',
        items: [
          {
            label: 'shared.service_contracts',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'service-contracts']
          },
          {
            label: 'shared.termination_of_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'termination-of-services']
          },
          {
            label: 'shared.igaza_certificates',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'igaza-certificates']
          },
          {
            label: 'education.addendum_igazas',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'addendum-igazas']
          },
          {
            label: 'shared.declaration_of_commitment_il_pd.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'declaration-of-commitment-il-pds']
          },
          {
            label: 'education.declaration_of_commitment.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'declaration-of-commitments']
          },
          {
            label: 'shared.child_allowance_application.short',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'child-allowance-applications']
          },
          {
            label: 'education.part_time_jobs',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'part-time-jobs']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      },
      {
        label: 'shared.applications_notifications',
        items: [
          {
            label: 'shared.absences',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'absences']
          },
          {
            label: 'education.transfer_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'transfer-services']
          },
          {
            label: 'education.proposal_honors',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'proposal-honors']
          },
          {
            id: 'hearings',
            label: 'education.hearings',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'hearings']
          },
          {
            label: 'education.hearing_sessions',
            icon: 'pi pi-fw pi-calendar',
            routerLink: ['/', 'hearing-sessions']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      },
      {
        label: 'shared.teaching_organisation',
        items: [
          {
            label: 'education.letter_of_services',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'letter-of-services']
          },
          {
            label: 'education.planning_forms',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'planning-forms']
          },
          {
            label: 'education.groups',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'groups']
          },
          {
            label: 'education.timetables',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'timetables']
          },
          {
            label: 'education.schools',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'schools']
          },
          {
            label: 'education.observation_sheets',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'observation-sheets']
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      },
      {
        label: 'shared.documents_templates',
        items: [
          {
            label: 'shared.documents',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'documents']
          },
          {
            label: 'print',
            icon: 'pi pi-fw pi-print',
            routerLink: ['/', 'print']
          },
          {
            label: 'shared.print_history',
            icon: 'pi pi-fw pi-history',
            routerLink: ['/', 'print-history']
          },
          {
            label: 'shared.print_templates',
            icon: 'pi pi-fw pi-file-pdf',
            routerLink: ['/', 'print-templates'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION }
          },
          {
            label: 'shared.mail_templates',
            icon: 'pi pi-fw pi-envelope',
            routerLink: ['/', 'mail-templates'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION }
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      },
      {
        label: 'shared.communication_planning',
        items: [
          {
            label: 'shared.announcements',
            icon: 'pi pi-fw pi-megaphone',
            routerLink: ['/', 'announcements'],
            state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
          },
          {
            label: 'shared.messages',
            icon: 'pi pi-inbox',
            routerLink: ['/', 'messages']
          },
          {
            label: 'shared.events',
            icon: 'pi pi-calendar-clock',
            routerLink: ['/', 'events']
          }
        ]
      },
      {
        label: 'shared.evaluation_contributions',
        items: [
          {
            label: 'shared.statistics',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'statistics'],
            state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
          },
          {
            label: 'shared.solidarity_contribution_bills',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'solidarity-contribution-bills'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION }
          },
          {
            label: 'shared.solidarity_contribution_bills',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'my', 'solidarity-contribution-bills'],
            state: { permissions: [RoleEnum.TEACHER, RoleEnum.INSPECTOR] }
          }
        ]
      },
      {
        label: 'shared.actors',
        items: [
          {
            label: 'shared.role.teachers',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'teachers'],
            state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
          },
          {
            label: 'shared.role.inspectors',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'inspectors'],
            state: { permissions: AuthorizationUtil.GROUP_SCHOOL }
          },
          {
            label: 'shared.role.school_offices',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'school-offices'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION }
          },
          {
            label: 'shared.role.education_office_assistants',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'education-office-assistants'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION_WITHOUT_ASSISTANT }
          },
          {
            label: 'shared.role.education_offices',
            icon: 'pi pi-fw pi-briefcase',
            routerLink: ['/', 'actors', 'education-offices'],
            state: { permissions: RoleEnum.MODERATOR }
          },
          {
            label: 'shared.people',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'actors', 'people'],
            state: { permissions: AuthorizationUtil.GROUP_EDUCATION }
          },
          {
            label: 'shared.school_enrolment_title',
            icon: 'pi pi-fw pi-inbox',
            routerLink: ['/', 'actors', 'school-enrolment-applications'],
            state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      },
      {
        label: 'shared.administration',
        items: [
          {
            label: 'shared.education',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'administration', 'education'],
            state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
          },
          {
            label: 'shared.actors',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/', 'administration', 'actors'],
            state: { permissions: AuthorizationUtil.GROUP_SCHOOL }
          }
        ],
        state: { permissions: AuthorizationUtil.GROUP_INSPECTOR }
      }
    ]);

    this.pendingService.init();
    this.hearingMenuService.init();
    this.eventService.init();
    this.educationService.init();
    this.notificationService.init();

    this.authorizationService.currentRole$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(role => {
        if (role) {
          this.graphQlService.init(() => role);
        }
      });

    this.loadingService.stopLoading();
  }
}
